import { c as _c } from "react/compiler-runtime";
import { useState, useId } from 'react';
import type { ReactNode, ElementType } from 'react';
import Collapse from '@mui/material/Collapse';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Divider from '@classify/react-core-components/Divider';
import { styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';
interface Props {
  children?: ReactNode;
  title: string;
  isCollapsable?: boolean;
  component?: ElementType;
}
const Container = styled(Box)(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  paddingLeft: 0,
  margin: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(2),
  [breakpoints.up('sm')]: {
    gap: spacing(1)
  }
}));
const TogglerButton = styled('button')(({
  theme: {
    spacing,
    transitions
  }
}) => ({
  width: '100%',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  height: 48,
  margin: spacing(1, 0),
  padding: 0,
  position: 'relative',
  '& > svg': {
    position: 'absolute',
    right: spacing(1),
    top: 11,
    transition: transitions.create('transform', {
      duration: transitions.duration.shortest
    })
  },
  '&[aria-expanded=true] > svg': {
    transform: 'rotate(-180deg)'
  }
}));
const Title = styled(Typography)(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  margin: spacing(2, 0),
  [breakpoints.up('sm')]: {
    margin: spacing(0, 0, 2, 0)
  }
})) as typeof Typography;
function NonCollapsableWrapper(t0) {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Title component="div" variant="overline">{title}</Title>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <>{t1}{children}</>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function CollapsableWrapper(t0) {
  const $ = _c(17);
  const {
    title,
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const uuid = useId();
  let t1;
  if ($[0] !== isOpen) {
    t1 = () => setIsOpen(!isOpen);
    $[0] = isOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClick = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      margin: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ExpandMore aria-hidden="true" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== handleClick || $[5] !== isOpen || $[6] !== title || $[7] !== uuid) {
    t4 = <Title component={TogglerButton} variant="overline" onClick={handleClick} aria-expanded={isOpen} aria-controls={uuid} sx={t2}>{title}{t3}</Title>;
    $[4] = handleClick;
    $[5] = isOpen;
    $[6] = title;
    $[7] = uuid;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== children || $[10] !== isOpen || $[11] !== uuid) {
    t5 = <Collapse in={isOpen} timeout="auto" id={uuid}>{children}</Collapse>;
    $[9] = children;
    $[10] = isOpen;
    $[11] = uuid;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Divider />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t5) {
    t7 = <>{t4}{t5}{t6}</>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export default function FooterItemsContainer(t0) {
  const $ = _c(7);
  const {
    children,
    title,
    isCollapsable,
    component: t1
  } = t0;
  const component = t1 === undefined ? "ul" : t1;
  const Wrapper = isCollapsable ? CollapsableWrapper : NonCollapsableWrapper;
  let t2;
  if ($[0] !== children || $[1] !== component) {
    t2 = <Container component={component}>{children}</Container>;
    $[0] = children;
    $[1] = component;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== Wrapper || $[4] !== t2 || $[5] !== title) {
    t3 = <div><Wrapper title={title}>{t2}</Wrapper></div>;
    $[3] = Wrapper;
    $[4] = t2;
    $[5] = title;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}